import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FiArrowUpRight, FiPause, FiPlay } from 'react-icons/fi';

export default function CompanyFilm({ reducedMotion = false }) {
  const video = useRef(null);
  const visible = useRef(false);
  const userPaused = useRef(false);
  const manuallyStarted = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const element = video.current;
    let disposed = false;
    const syncPlayback = () => {
      const automatic = !reducedMotion && !navigator.connection?.saveData;
      const canPlay = visible.current && !document.hidden && !userPaused.current && (automatic || manuallyStarted.current);
      if (!canPlay) { element.pause(); return; }
      element.play()?.then(() => {
        if (disposed || !visible.current || document.hidden || userPaused.current) element.pause();
      }).catch(() => { if (!disposed) setPlaying(false); });
    };
    const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(entries => {
      visible.current = entries[0].isIntersecting;
      syncPlayback();
    }, { threshold: 0.15 }) : null;
    observer?.observe(element);
    document.addEventListener('visibilitychange', syncPlayback);
    return () => {
      disposed = true;
      observer?.disconnect();
      document.removeEventListener('visibilitychange', syncPlayback);
      element.pause();
    };
  }, [reducedMotion]);

  async function togglePlayback() {
    const element = video.current;
    if (!element.paused) {
      userPaused.current = true;
      element.pause();
      return;
    }
    userPaused.current = false;
    manuallyStarted.current = true;
    try { await element.play(); } catch { setPlaying(false); }
  }

  return <section className="v-company-film" id="company-film" aria-labelledby="v-film-title" data-ready={ready && !failed}>
    <div className="v-film-backdrop" aria-hidden="true">
      <img src="/assets/voices/company-poster.webp" alt="" width="1280" height="720" loading="lazy" decoding="async" />
      <video ref={video} playsInline muted loop disablePictureInPicture tabIndex={-1} preload="none" poster="/assets/voices/company-poster.webp"
        onPlay={() => { setPlaying(true); setReady(true); }} onPause={() => setPlaying(false)} onError={() => setFailed(true)} aria-label="Life at Antrazal">
        <source src="/assets/voices/company-background.mp4" type="video/mp4" />
      </video>
    </div>
    <div className="v-film-heading">
      <p className="v-label">Who we are</p>
      <h2 id="v-film-title">Technology connects.<br /><em>People make it work.</em></h2>
      <p className="v-film-statement">Antrazal is built on trust, powered by people, and driven by innovation delivering solutions that create real and lasting impact.</p>
      <Link to="/who-we-are" className="v-film-team-link">Meet the team <FiArrowUpRight aria-hidden="true" /></Link>
    </div>
    <div className="v-film-bottom"><p>Trust. Innovation. People.</p>
      {!failed && <button type="button" className="v-film-play" onClick={togglePlayback} aria-label={`${playing ? 'Pause' : 'Play'} background video`} title={`${playing ? 'Pause' : 'Play'} background video`}>
        {playing ? <FiPause aria-hidden="true" /> : <FiPlay aria-hidden="true" />}<span>{playing ? 'Pause background' : 'Play background'}</span>
      </button>}
    </div>
    {failed && <p className="v-film-error" role="status">The film could not load. <Link to="/who-we-are">Meet our team</Link></p>}
  </section>;
}
